import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdAdd, MdDelete, MdEdit, MdCheck, MdClose, MdSearch } from 'react-icons/md';

export default function ManageLocations() {
  const location = useLocation();
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [cities, setCities] = useState([]);

  const [statesLoading, setStatesLoading] = useState(true);
  const [citiesLoading, setCitiesLoading] = useState(false);

  const [stateSearch, setStateSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchFromUrl = searchParams.get('search') || searchParams.get('q') || '';
    if (searchFromUrl) {
      setStateSearch(searchFromUrl);
      setCitySearch(searchFromUrl);
    }
  }, [location.search]);

  // State CRUD
  const [showStateForm, setShowStateForm] = useState(false);
  const [stateForm, setStateForm] = useState({ name: '' });
  const [editingStateId, setEditingStateId] = useState(null);
  const [editStateForm, setEditStateForm] = useState({ name: '' });
  const [deleteStateModal, setDeleteStateModal] = useState(null);

  // City CRUD
  const [showCityForm, setShowCityForm] = useState(false);
  const [cityForm, setCityForm] = useState({ name: '', slug: '', state_id: '' });
  const [editingCityId, setEditingCityId] = useState(null);
  const [editCityForm, setEditCityForm] = useState({ name: '', slug: '', state_id: '' });
  const [deleteCityModal, setDeleteCityModal] = useState(null);

  const [deleting, setDeleting] = useState(false);

  const fetchStates = async (targetSelectId = null) => {
    try {
      const res = await api.get('/states');
      const rawData = res.data.data || [];
      const getPriority = (name) => {
        const n = (name || '').toLowerCase().trim();
        if (n === 'all india') return 1;
        if (n === 'telangana') return 2;
        if (n === 'andhra pradesh') return 3;
        return 999;
      };
      const data = [...rawData].sort((a, b) => {
        const pA = getPriority(a.name);
        const pB = getPriority(b.name);
        if (pA !== pB) return pA - pB;
        return (a.name || '').localeCompare(b.name || '');
      });

      setStates(data);
      if (data.length > 0) {
        if (targetSelectId) {
          const match = data.find(s => s.id === targetSelectId);
          if (match) setSelectedState(match);
        } else if (!selectedState) {
          setSelectedState(data[0]);
        } else {
          const match = data.find(s => s.id === selectedState.id);
          if (match) setSelectedState(match);
          else setSelectedState(data[0]);
        }
      } else {
        setSelectedState(null);
        setCities([]);
      }
    } catch (err) {
      toast.error('Failed to load states');
    } finally {
      setStatesLoading(false);
    }
  };

  const fetchStateCities = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }
    setCitiesLoading(true);
    try {
      const res = await api.get(`/states/${stateId}/cities`);
      setCities(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load cities');
    } finally {
      setCitiesLoading(false);
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    if (selectedState) {
      fetchStateCities(selectedState.id);
    }
  }, [selectedState?.id]);

  // State actions
  const handleCreateState = async (e) => {
    e.preventDefault();
    if (!stateForm.name.trim()) return toast.error('State name is required');
    try {
      const res = await api.post('/states', stateForm);
      toast.success('State added');
      setStateForm({ name: '' });
      setShowStateForm(false);
      fetchStates(res.data.data.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add state');
    }
  };

  const handleSaveStateEdit = async (id) => {
    try {
      await api.put(`/states/${id}`, editStateForm);
      toast.success('State updated');
      setEditingStateId(null);
      fetchStates(selectedState?.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update state');
    }
  };

  const confirmDeleteState = async () => {
    if (!deleteStateModal) return;
    setDeleting(true);
    try {
      await api.delete(`/states/${deleteStateModal.id}`);
      toast.success('State deleted');
      setDeleteStateModal(null);
      fetchStates();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete state failed');
    } finally {
      setDeleting(false);
    }
  };

  // City actions
  const handleOpenAddCity = () => {
    if (!selectedState) return toast.error('Please select a state first');
    setCityForm({
      name: '',
      slug: '',
      state_id: selectedState.id,
    });
    setShowCityForm(true);
  };

  const handleCreateCity = async (e) => {
    e.preventDefault();
    if (!cityForm.name.trim()) return toast.error('City name is required');
    try {
      await api.post('/cities', {
        ...cityForm,
        state_id: cityForm.state_id || selectedState?.id,
      });
      toast.success('City added');
      setCityForm({ name: '', slug: '', state_id: selectedState?.id || '' });
      setShowCityForm(false);
      if (selectedState) fetchStateCities(selectedState.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add city');
    }
  };

  const startEditCity = (city) => {
    setEditingCityId(city.id);
    setEditCityForm({
      name: city.name || '',
      slug: city.slug || '',
      state_id: city.state_id || (selectedState ? selectedState.id : ''),
    });
  };

  const handleSaveCityEdit = async (id) => {
    try {
      await api.put(`/cities/${id}`, editCityForm);
      toast.success('City updated');
      setEditingCityId(null);
      if (selectedState) fetchStateCities(selectedState.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update city');
    }
  };

  const confirmDeleteCity = async () => {
    if (!deleteCityModal) return;
    setDeleting(true);
    try {
      await api.delete(`/cities/${deleteCityModal.id}`);
      toast.success('City deleted');
      setDeleteCityModal(null);
      if (selectedState) fetchStateCities(selectedState.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete city failed');
    } finally {
      setDeleting(false);
    }
  };

  const filteredStates = states.filter(s =>
    (s.name || '').toLowerCase().includes(stateSearch.toLowerCase()) ||
    (s.slug || '').toLowerCase().includes(stateSearch.toLowerCase())
  );

  const filteredCities = cities.filter(c =>
    (c.name || '').toLowerCase().includes(citySearch.toLowerCase()) ||
    (c.slug || '').toLowerCase().includes(citySearch.toLowerCase())
  );

  return (
    <div className="page" style={{ paddingBottom: '20px' }}>
      <div className="page-header" style={{ marginBottom: '16px' }}>
        <h2>Manage Location Data</h2>
      </div>

      <div className="manage-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px', alignItems: 'stretch' }}>
        {/* Left Section - States */}
        <div className="card" style={{ marginBottom: 0, padding: '20px', display: 'flex', flexDirection: 'column', minHeight: '780px', maxHeight: '840px' }}>
          <div className="manage-section" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className="section-header" style={{ marginBottom: '14px', flexShrink: 0 }}>
              <h3 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>States ({states.length})</h3>
              <div className="flex-row items-center gap-2">
                <div className="search-input-wrap" style={{ maxWidth: '170px' }}>
                  <MdSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search state..."
                    value={stateSearch}
                    onChange={e => setStateSearch(e.target.value)}
                    className="search-input"
                    style={{ fontSize: '14px' }}
                  />
                </div>
                <button className="btn btn-sm btn-primary" onClick={() => setShowStateForm(!showStateForm)} style={{ fontSize: '14px' }}>
                  {showStateForm ? <><MdClose /> Cancel</> : <><MdAdd /> Add State</>}
                </button>
              </div>
            </div>

            {showStateForm && (
              <form className="manage-form mb-3" onSubmit={handleCreateState} style={{ flexShrink: 0 }}>
                <div className="form-group mb-2">
                  <input
                    type="text"
                    placeholder="State name (e.g. Telangana)"
                    value={stateForm.name}
                    onChange={e => setStateForm({ name: e.target.value })}
                    style={{ fontSize: '14px' }}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-sm btn-primary" style={{ fontSize: '14px' }}>Save State</button>
              </form>
            )}

            {statesLoading ? <p className="text-muted" style={{ fontSize: '14px' }}>Loading states...</p> : (
              <div className="manage-list" style={{ flex: 1, maxHeight: '700px', overflowY: 'auto', paddingRight: '4px' }}>
                {filteredStates.map(st => {
                  const isSelected = selectedState && selectedState.id === st.id;
                  return (
                    <div
                      key={st.id}
                      className="manage-item"
                      onClick={() => setSelectedState(st)}
                      style={{
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        borderLeft: isSelected ? '4px solid #EF233C' : '1px solid #E5E7EB',
                        backgroundColor: isSelected ? '#FFF1F2' : '#FFFFFF',
                        borderColor: isSelected ? '#EF233C' : '#E5E7EB',
                        marginBottom: '4px',
                        padding: '8px 12px',
                        minHeight: '42px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {editingStateId === st.id ? (
                        <div className="manage-edit-row" onClick={e => e.stopPropagation()} style={{ width: '100%' }}>
                          <input
                            type="text"
                            value={editStateForm.name}
                            onChange={e => setEditStateForm({ name: e.target.value })}
                            style={{ fontSize: '15px' }}
                          />
                          <div className="flex-row gap-2 mt-1">
                            <button className="btn btn-sm btn-primary" onClick={() => handleSaveStateEdit(st.id)}><MdCheck /></button>
                            <button className="btn btn-sm btn-ghost" onClick={() => setEditingStateId(null)}><MdClose /></button>
                          </div>
                        </div>
                      ) : (
                        <div className="manage-display-row" style={{ width: '100%' }}>
                          <div className="manage-item-info">
                            <span className="manage-name" style={{ color: isSelected ? '#EF233C' : '#111827', fontWeight: isSelected ? 700 : 500, fontSize: '16px' }}>
                              {st.name}
                            </span>
                            <span className="manage-slug" style={{ fontSize: '12px' }}>{st.slug}</span>
                            {isSelected && <span className="badge badge-success ml-2" style={{ fontSize: '12px', padding: '3px 8px' }}>Selected</span>}
                          </div>
                          <div className="action-btns" onClick={e => e.stopPropagation()}>
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={() => {
                                setEditingStateId(st.id);
                                setEditStateForm({ name: st.name });
                              }}
                            >
                              <MdEdit />
                            </button>
                            <button className="btn btn-sm btn-danger" onClick={() => setDeleteStateModal(st)}>
                              <MdDelete />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {filteredStates.length === 0 && <p className="text-muted" style={{ fontSize: '14px' }}>No states found</p>}
              </div>
            )}
          </div>
        </div>

        {/* Right Section - Cities */}
        <div className="card" style={{ marginBottom: 0, padding: '20px', display: 'flex', flexDirection: 'column', minHeight: '780px', maxHeight: '840px' }}>
          <div className="manage-section" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className="section-header" style={{ marginBottom: '14px', flexShrink: 0 }}>
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>
                  Cities {selectedState ? `in ${selectedState.name}` : ''}
                </h3>
                {selectedState && <span className="text-muted" style={{ fontSize: '14px' }}>Selected State: {selectedState.name}</span>}
              </div>
              <div className="flex-row items-center gap-2">
                <div className="search-input-wrap" style={{ maxWidth: '170px' }}>
                  <MdSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search city..."
                    value={citySearch}
                    onChange={e => setCitySearch(e.target.value)}
                    className="search-input"
                    style={{ fontSize: '14px' }}
                  />
                </div>
                <button className="btn btn-sm btn-primary" onClick={handleOpenAddCity} disabled={!selectedState} style={{ fontSize: '14px' }}>
                  <MdAdd /> Add City
                </button>
              </div>
            </div>

            {showCityForm && (
              <form className="manage-form mb-4" onSubmit={handleCreateCity} style={{ flexShrink: 0 }}>
                <div className="form-group mb-2">
                  <label className="font-bold text-muted uppercase mb-1" style={{ fontSize: '14px' }}>State (Auto-selected)</label>
                  <select
                    value={cityForm.state_id}
                    onChange={e => setCityForm(p => ({ ...p, state_id: Number(e.target.value) }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #E5E7EB', marginBottom: '8px', fontSize: '14px' }}
                  >
                    {states.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group mb-2">
                  <input
                    type="text"
                    placeholder="City name (e.g. Hyderabad)"
                    value={cityForm.name}
                    onChange={e => setCityForm(p => ({ ...p, name: e.target.value }))}
                    style={{ fontSize: '14px' }}
                    required
                  />
                </div>
                <div className="flex-row gap-2">
                  <button type="submit" className="btn btn-sm btn-primary" style={{ fontSize: '14px' }}>Save City</button>
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowCityForm(false)} style={{ fontSize: '14px' }}>Cancel</button>
                </div>
              </form>
            )}

            {citiesLoading ? <p className="text-muted" style={{ fontSize: '14px' }}>Loading cities for {selectedState?.name}...</p> : (
              <div className="manage-list" style={{ flex: 1, maxHeight: '700px', overflowY: 'auto', paddingRight: '4px' }}>
                {filteredCities.map(c => (
                  <div
                    key={c.id}
                    className="manage-item"
                    style={{
                      marginBottom: '4px',
                      padding: '8px 12px',
                      minHeight: '42px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {editingCityId === c.id ? (
                      <div className="manage-edit-row" style={{ width: '100%' }}>
                        <div className="form-group mb-2">
                          <label className="text-muted mb-1" style={{ fontSize: '14px' }}>State</label>
                          <select
                            value={editCityForm.state_id}
                            onChange={e => setEditCityForm(p => ({ ...p, state_id: Number(e.target.value) }))}
                            style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #E5E7EB', fontSize: '14px' }}
                          >
                            {states.map(s => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group mb-2">
                          <input
                            type="text"
                            placeholder="City Name"
                            value={editCityForm.name}
                            onChange={e => setEditCityForm(p => ({ ...p, name: e.target.value }))}
                            style={{ fontSize: '15px' }}
                          />
                        </div>
                        <div className="flex-row gap-2 mt-2">
                          <button className="btn btn-sm btn-primary" onClick={() => handleSaveCityEdit(c.id)}><MdCheck /></button>
                          <button className="btn btn-sm btn-ghost" onClick={() => setEditingCityId(null)}><MdClose /></button>
                        </div>
                      </div>
                    ) : (
                      <div className="manage-display-row" style={{ width: '100%' }}>
                        <div className="manage-item-info">
                          <span className="manage-name" style={{ fontSize: '16px' }}>{c.name}</span>
                          <span className="manage-slug" style={{ fontSize: '12px' }}>{c.slug}</span>
                          {c.State && <span className="manage-sub" style={{ fontSize: '12px' }}>{c.State.name}</span>}
                        </div>
                        <div className="action-btns">
                          <button className="btn btn-sm btn-ghost" onClick={() => startEditCity(c)}><MdEdit /></button>
                          <button className="btn btn-sm btn-danger" onClick={() => setDeleteCityModal(c)}><MdDelete /></button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {filteredCities.length === 0 && (
                  <p className="text-muted" style={{ fontSize: '14px' }}>
                    {selectedState ? `No cities found in ${selectedState.name}` : 'Select a state to view cities'}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* State Delete Modal */}
      {deleteStateModal && (
        <div className="modal-overlay" onClick={() => setDeleteStateModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete State</h3>
              <button className="modal-close" onClick={() => setDeleteStateModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete state <strong>"{deleteStateModal.name}"</strong>?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteStateModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDeleteState} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* City Delete Modal */}
      {deleteCityModal && (
        <div className="modal-overlay" onClick={() => setDeleteCityModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete City</h3>
              <button className="modal-close" onClick={() => setDeleteCityModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete city <strong>"{deleteCityModal.name}"</strong>?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteCityModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDeleteCity} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
